/* Admin → Byers MSRP Pricing
   PDF-first per-VIN pricing enrichment workflow:
     1. Upload the Byers MSRP PDF (export from byerschevy.com).
     2. We parse all per-vehicle blocks and match them to live vAuto
        inventory by VIN (primary) or stock number (fallback).
     3. Admin reviews the preview — totals, match counts, and a flagged
        list of pricing mismatches (PDF Byers Price ≠ vAuto Price).
     4. Publish writes msrp / dealer_discount / byers_pdf_offers /
        byers_pdf_special_financing to db.vehicles.
   Critical rule: vAuto price is NEVER overwritten. Conditional offers +
   special financing are display-only on the VDP — never subtracted.
*/
import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";

const fmtMoney = (n) =>
  n === null || n === undefined ? "—" : `$${Number(n).toLocaleString()}`;

function Tile({ label, value, accent, testId }) {
  return (
    <div
      className="rounded-xl border border-[var(--ssc-line)] bg-white p-4 shadow-sm"
      data-testid={testId}
    >
      <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-ink-2)]">
        {label}
      </div>
      <div
        className={`mt-1 text-2xl font-black tabular-nums ${
          accent || "text-[var(--ssc-navy)]"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const m = {
    matched: "bg-emerald-100 text-emerald-700",
    no_match: "bg-red-100 text-red-700",
  };
  return (
    <span
      className={`text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-full ${
        m[status] || "bg-slate-100 text-slate-700"
      }`}
    >
      {status === "matched" ? "Matched" : "No match"}
    </span>
  );
}

export default function AdminByersPricing() {
  const [tab, setTab] = useState("upload");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [uploadResult, setUploadResult] = useState(null);
  const [preview, setPreview] = useState(null);
  const [filter, setFilter] = useState("all"); // all | matched | no_match | mismatch
  const [history, setHistory] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);
  const [mismatches, setMismatches] = useState([]);
  const [mismatchBusyVin, setMismatchBusyVin] = useState(null);

  const loadMismatches = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/byers-pricing/mismatches");
      setMismatches(data.mismatches || []);
    } catch (e) {
      // ignore
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/byers-pricing/uploads");
      setHistory(data.uploads || []);
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    loadHistory();
    loadMismatches();
  }, [loadHistory, loadMismatches]);

  async function handleMismatchAction(vin, action) {
    setMismatchBusyVin(vin);
    try {
      const body = action === "reviewed"
        ? { status: "reviewed" }
        : action === "open"
          ? { status: "open" }
          : action === "ignore"
            ? { ignored: true }
            : action === "unignore"
              ? { ignored: false }
              : null;
      if (!body) return;
      await api.post(`/admin/byers-pricing/mismatches/${vin}/action`, body);
      await loadMismatches();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setMismatchBusyVin(null);
    }
  }

  async function handleFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    setError("");
    setPreview(null);
    setUploadResult(null);
    setPublishResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(
        "/admin/byers-pricing/upload-pdf",
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setUploadResult(data);
      // Auto-load full preview
      const { data: full } = await api.get(
        `/admin/byers-pricing/preview/${data.upload_id}`
      );
      setPreview(full);
      loadHistory();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Upload failed.");
    } finally {
      setBusy(false);
    }
  }

  async function openHistory(uploadId) {
    setBusy(true);
    setError("");
    setUploadResult(null);
    setPublishResult(null);
    try {
      const { data } = await api.get(
        `/admin/byers-pricing/preview/${uploadId}`
      );
      setPreview(data);
      setTab("upload");
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  }

  async function handlePublish() {
    if (!preview) return;
    if (preview.published) {
      setError("This upload is already published.");
      return;
    }
    if (
      !window.confirm(
        `Publish enrichment for ${preview.match_counts?.total || 0} parsed vehicles? vAuto prices are NOT overwritten. This will write MSRP, Dealer Discount, Other Offers, and Special Financing to matched vehicles.`
      )
    ) {
      return;
    }
    setPublishing(true);
    setError("");
    try {
      const { data } = await api.post(
        `/admin/byers-pricing/preview/${preview.id}/publish`
      );
      setPublishResult(data);
      // refresh preview to show published state
      const { data: full } = await api.get(
        `/admin/byers-pricing/preview/${preview.id}`
      );
      setPreview(full);
      loadHistory();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Publish failed.");
    } finally {
      setPublishing(false);
    }
  }

  async function handleDelete(uploadId) {
    if (!window.confirm("Delete this upload from history?")) return;
    try {
      await api.delete(`/admin/byers-pricing/uploads/${uploadId}`);
      loadHistory();
      if (preview?.id === uploadId) setPreview(null);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    }
  }

  const filteredVehicles =
    preview?.vehicles?.filter((v) => {
      if (filter === "matched") return v.match_status === "matched";
      if (filter === "no_match") return v.match_status === "no_match";
      if (filter === "mismatch") return !!v.pricing_mismatch_flag;
      return true;
    }) || [];

  return (
    <div className="space-y-6" data-testid="admin-byers-pricing">
      <header>
        <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)]">
          Pricing
        </div>
        <h1 className="text-3xl font-black text-[var(--ssc-navy)] mt-1">
          Byers MSRP Pricing Import
        </h1>
        <p className="text-[var(--ssc-ink-2)] mt-2 max-w-3xl">
          Upload the Byers MSRP PDF (from byerschevy.com) to enrich each new
          vehicle with its <strong>MSRP</strong>,{" "}
          <strong>Dealer Discount</strong>,{" "}
          <strong>Other Offers You May Qualify For</strong>, and{" "}
          <strong>Special Financing</strong>. The vAuto-displayed price is
          never overwritten — this is enrichment only.
        </p>
      </header>

      <nav className="flex gap-2 border-b border-[var(--ssc-line)]">
        {[
          ["upload", "Upload & Review"],
          ["mismatches", `Mismatches${mismatches.length ? ` (${mismatches.length})` : ""}`],
          ["history", "Upload History"],
        ].map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm font-bold border-b-2 -mb-px ${
              tab === k
                ? "border-[var(--ssc-blue)] text-[var(--ssc-navy)]"
                : "border-transparent text-[var(--ssc-ink-2)] hover:text-[var(--ssc-ink)]"
            }`}
            data-testid={`byers-tab-${k}`}
          >
            {label}
          </button>
        ))}
      </nav>

      {error && (
        <div
          className="rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm px-3 py-2"
          data-testid="byers-error"
        >
          {error}
        </div>
      )}

      {tab === "upload" && (
        <>
          <section className="rounded-xl border border-[var(--ssc-line)] bg-white p-5 shadow-sm">
            <h2 className="font-extrabold text-[var(--ssc-navy)] text-lg">
              Upload Byers MSRP PDF
            </h2>
            <p className="text-sm text-[var(--ssc-ink-2)] mt-1">
              Accepts the per-VIN PDF export from byerschevy.com (up to 25 MB).
              Parsing runs immediately — nothing is published until you press
              Publish.
            </p>
            <div className="mt-4 flex flex-wrap gap-3 items-center">
              <label
                className={`ssc-btn ssc-btn-primary cursor-pointer ${
                  busy ? "opacity-50 pointer-events-none" : ""
                }`}
                data-testid="byers-upload-button"
              >
                {busy ? "Parsing..." : "Choose PDF"}
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={handleFile}
                  className="hidden"
                />
              </label>
              {uploadResult && (
                <span className="text-sm text-[var(--ssc-ink-2)]">
                  Parsed{" "}
                  <strong className="text-[var(--ssc-navy)]">
                    {uploadResult.stats?.total || 0}
                  </strong>{" "}
                  vehicles
                </span>
              )}
            </div>
          </section>

          {preview && (
            <>
              <section className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Tile
                  label="Total Parsed"
                  value={preview.match_counts?.total ?? 0}
                  testId="byers-stat-total"
                />
                <Tile
                  label="Matched (VIN)"
                  value={preview.match_counts?.matched_vin ?? 0}
                  accent="text-emerald-700"
                  testId="byers-stat-matched-vin"
                />
                <Tile
                  label="Matched (Stock)"
                  value={preview.match_counts?.matched_stock_fallback ?? 0}
                  accent="text-emerald-700"
                  testId="byers-stat-matched-stock"
                />
                <Tile
                  label="No Match"
                  value={preview.match_counts?.no_match ?? 0}
                  accent="text-red-700"
                  testId="byers-stat-no-match"
                />
                <Tile
                  label="Price Mismatches"
                  value={preview.match_counts?.pricing_mismatch ?? 0}
                  accent="text-amber-700"
                  testId="byers-stat-mismatch"
                />
              </section>

              {(preview.warnings || []).length > 0 && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
                  {preview.warnings.join(" • ")}
                </div>
              )}

              <section className="rounded-xl border border-[var(--ssc-line)] bg-white p-5 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h2 className="font-extrabold text-[var(--ssc-navy)] text-lg">
                    Parsed Vehicles
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {[
                      ["all", "All"],
                      ["matched", "Matched"],
                      ["no_match", "No match"],
                      ["mismatch", "Mismatch"],
                    ].map(([k, label]) => (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setFilter(k)}
                        className={`px-3 py-1 text-xs font-bold rounded-full border ${
                          filter === k
                            ? "bg-[var(--ssc-navy)] text-white border-[var(--ssc-navy)]"
                            : "bg-white text-[var(--ssc-ink-2)] border-[var(--ssc-line)] hover:border-[var(--ssc-ink-2)]"
                        }`}
                        data-testid={`byers-filter-${k}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-4 overflow-x-auto">
                  <table
                    className="w-full text-sm border-collapse"
                    data-testid="byers-vehicle-table"
                  >
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wide text-[var(--ssc-ink-2)] border-b border-[var(--ssc-line)]">
                        <th className="py-2 pr-3">Status</th>
                        <th className="py-2 pr-3">Year/Model/Trim</th>
                        <th className="py-2 pr-3">Stock</th>
                        <th className="py-2 pr-3">VIN</th>
                        <th className="py-2 pr-3 text-right">MSRP</th>
                        <th className="py-2 pr-3 text-right">Discount</th>
                        <th className="py-2 pr-3 text-right">PDF Price</th>
                        <th className="py-2 pr-3 text-right">vAuto Price</th>
                        <th className="py-2 pr-3 text-center">Offers</th>
                        <th className="py-2 pr-3 text-center">Fin.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredVehicles.slice(0, 500).map((v) => (
                        <tr
                          key={v.vin}
                          className={`border-b border-[var(--ssc-line)]/50 ${
                            v.pricing_mismatch_flag ? "bg-amber-50/30" : ""
                          }`}
                          data-testid={`byers-row-${v.vin}`}
                        >
                          <td className="py-2 pr-3">
                            <StatusBadge status={v.match_status} />
                            {v.pricing_mismatch_flag && (
                              <span
                                className="ml-1 text-[10px] font-extrabold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800"
                                data-testid={`byers-mismatch-${v.vin}`}
                              >
                                ⚠
                              </span>
                            )}
                          </td>
                          <td className="py-2 pr-3 font-bold text-[var(--ssc-ink)]">
                            {v.year} {v.model} {v.trim}
                          </td>
                          <td className="py-2 pr-3 tabular-nums text-[var(--ssc-ink-2)]">
                            {v.stock_number || "—"}
                          </td>
                          <td className="py-2 pr-3 tabular-nums text-[var(--ssc-ink-2)] font-mono text-xs">
                            {v.vin}
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums">
                            {fmtMoney(v.msrp)}
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums text-red-700">
                            {v.dealer_discount ? `−${fmtMoney(v.dealer_discount)}` : "—"}
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums">
                            {fmtMoney(v.byers_pdf_price)}
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums font-bold">
                            {fmtMoney(v.vauto_price)}
                          </td>
                          <td className="py-2 pr-3 text-center text-[var(--ssc-ink-2)]">
                            {v.offers?.length || 0}
                          </td>
                          <td className="py-2 pr-3 text-center text-[var(--ssc-ink-2)]">
                            {v.special_financing?.length || 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredVehicles.length === 0 && (
                    <p className="text-center text-sm text-[var(--ssc-ink-2)] py-6">
                      No vehicles match this filter.
                    </p>
                  )}
                  {filteredVehicles.length > 500 && (
                    <p className="text-xs text-[var(--ssc-ink-2)] mt-2">
                      Showing first 500 of {filteredVehicles.length} rows.
                    </p>
                  )}
                </div>
              </section>

              <section className="rounded-xl border border-[var(--ssc-line)] bg-white p-5 shadow-sm">
                <h2 className="font-extrabold text-[var(--ssc-navy)] text-lg">
                  Publish
                </h2>
                {preview.published ? (
                  <div
                    className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-900"
                    data-testid="byers-published-status"
                  >
                    Published {preview.published_at?.slice(0, 19)} ·{" "}
                    {preview.published_count} vehicles updated ·{" "}
                    {preview.published_mismatches?.length || 0} mismatch flags
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-[var(--ssc-ink-2)] mt-1">
                      Writes MSRP, Dealer Discount, Other Offers, and Special
                      Financing to matched vehicles in the live inventory. The{" "}
                      <strong>vAuto Price field is NOT touched</strong>. Mismatch
                      flags will appear on the VDP for any vehicle where the PDF
                      Byers Price doesn&apos;t match the live vAuto price.
                    </p>
                    <button
                      type="button"
                      onClick={handlePublish}
                      disabled={publishing}
                      className="mt-3 ssc-btn ssc-btn-primary"
                      data-testid="byers-publish-button"
                    >
                      {publishing
                        ? "Publishing..."
                        : `Publish ${preview.match_counts?.matched_vin + preview.match_counts?.matched_stock_fallback || 0} matched vehicles`}
                    </button>
                  </>
                )}
                {publishResult && (
                  <div
                    className="mt-3 rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-blue-900"
                    data-testid="byers-publish-result"
                  >
                    Wrote {publishResult.written} vehicles • Skipped{" "}
                    {publishResult.skipped} • Mismatches flagged{" "}
                    {publishResult.mismatches_flagged}
                  </div>
                )}
              </section>
            </>
          )}
        </>
      )}

      {tab === "mismatches" && (
        <section
          className="rounded-xl border border-[var(--ssc-line)] bg-white p-5 shadow-sm"
          data-testid="byers-mismatches"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-extrabold text-[var(--ssc-navy)] text-lg">
                Price Mismatches (Admin Only)
              </h2>
              <p className="text-sm text-[var(--ssc-ink-2)] mt-1 max-w-3xl">
                Vehicles where the PDF Byers Price did not reconcile with the
                live vAuto price. <strong>vAuto remains the public price</strong>{" "}
                — no customer-facing display is changed by this report. Use
                Mark Reviewed when you have validated the gap, or Ignore VIN to
                hide a known intentional difference.
              </p>
            </div>
            <button
              type="button"
              onClick={loadMismatches}
              className="ssc-btn ssc-btn-outline"
              data-testid="byers-mismatches-refresh"
            >
              Refresh
            </button>
          </div>

          {mismatches.length === 0 ? (
            <p
              className="mt-4 text-sm text-[var(--ssc-ink-2)]"
              data-testid="byers-mismatches-empty"
            >
              No active price mismatches. Re-import a fresh PDF if you expect
              changes to surface here.
            </p>
          ) : (
            <div className="mt-4 overflow-x-auto">
              <table
                className="w-full text-sm border-collapse"
                data-testid="byers-mismatches-table"
              >
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wide text-[var(--ssc-ink-2)] border-b border-[var(--ssc-line)]">
                    <th className="py-2 pr-3">Stock</th>
                    <th className="py-2 pr-3">VIN</th>
                    <th className="py-2 pr-3">Year/Model/Trim</th>
                    <th className="py-2 pr-3 text-right">PDF Price</th>
                    <th className="py-2 pr-3 text-right">vAuto Price</th>
                    <th className="py-2 pr-3 text-right">Diff</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {mismatches.map((m) => {
                    const status = m.pricing_mismatch_ignored
                      ? "ignored"
                      : m.pricing_mismatch_review_status === "reviewed"
                        ? "reviewed"
                        : "open";
                    return (
                      <tr
                        key={m.vin}
                        className={`border-b border-[var(--ssc-line)]/50 ${status === "ignored" ? "opacity-60" : ""}`}
                        data-testid={`byers-mismatch-row-${m.vin}`}
                      >
                        <td className="py-2 pr-3 font-bold tabular-nums">
                          {m.stock_number || "—"}
                        </td>
                        <td className="py-2 pr-3 text-[var(--ssc-ink-2)] font-mono text-xs">
                          {m.vin}
                        </td>
                        <td className="py-2 pr-3">
                          {[m.year, m.make, m.model, m.trim].filter(Boolean).join(" ")}
                          <div className="text-[10px] text-[var(--ssc-ink-2)] mt-0.5">
                            {m.possible_reason}
                          </div>
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums">
                          {fmtMoney(m.byers_pdf_price)}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums font-bold text-[var(--ssc-navy)]">
                          {fmtMoney(m.price)}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums">
                          {m.diff == null
                            ? "—"
                            : `${m.diff > 0 ? "+" : ""}${fmtMoney(m.diff)}`}
                        </td>
                        <td className="py-2 pr-3">
                          {status === "ignored" && (
                            <span className="text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                              Ignored
                            </span>
                          )}
                          {status === "reviewed" && (
                            <span className="text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                              Reviewed
                            </span>
                          )}
                          {status === "open" && (
                            <span className="text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                              Open
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-3">
                          <div className="flex flex-wrap gap-2">
                            {status !== "reviewed" && (
                              <button
                                type="button"
                                disabled={mismatchBusyVin === m.vin}
                                onClick={() => handleMismatchAction(m.vin, "reviewed")}
                                className="text-emerald-700 font-bold hover:underline disabled:opacity-50"
                                data-testid={`byers-mismatch-review-${m.vin}`}
                              >
                                Mark Reviewed
                              </button>
                            )}
                            {status === "reviewed" && (
                              <button
                                type="button"
                                disabled={mismatchBusyVin === m.vin}
                                onClick={() => handleMismatchAction(m.vin, "open")}
                                className="text-amber-700 font-bold hover:underline disabled:opacity-50"
                                data-testid={`byers-mismatch-reopen-${m.vin}`}
                              >
                                Re-open
                              </button>
                            )}
                            {!m.pricing_mismatch_ignored ? (
                              <button
                                type="button"
                                disabled={mismatchBusyVin === m.vin}
                                onClick={() => handleMismatchAction(m.vin, "ignore")}
                                className="text-slate-600 font-bold hover:underline disabled:opacity-50"
                                data-testid={`byers-mismatch-ignore-${m.vin}`}
                              >
                                Ignore VIN
                              </button>
                            ) : (
                              <button
                                type="button"
                                disabled={mismatchBusyVin === m.vin}
                                onClick={() => handleMismatchAction(m.vin, "unignore")}
                                className="text-blue-700 font-bold hover:underline disabled:opacity-50"
                                data-testid={`byers-mismatch-unignore-${m.vin}`}
                              >
                                Un-ignore
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="mt-4 text-[11px] text-[var(--ssc-ink-2)]">
            vAuto price is never overwritten by this tool. To resolve a mismatch
            permanently, re-export a fresh Byers MSRP PDF and re-upload it on
            the Upload &amp; Review tab.
          </p>
        </section>
      )}

      {tab === "history" && (
        <section className="rounded-xl border border-[var(--ssc-line)] bg-white p-5 shadow-sm">
          <h2 className="font-extrabold text-[var(--ssc-navy)] text-lg">
            Upload History
          </h2>
          {history.length === 0 ? (
            <p className="text-sm text-[var(--ssc-ink-2)] mt-3">
              No uploads yet.
            </p>
          ) : (
            <table
              className="w-full text-sm mt-4 border-collapse"
              data-testid="byers-history-table"
            >
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wide text-[var(--ssc-ink-2)] border-b border-[var(--ssc-line)]">
                  <th className="py-2 pr-3">Uploaded</th>
                  <th className="py-2 pr-3">File</th>
                  <th className="py-2 pr-3 text-right">Total</th>
                  <th className="py-2 pr-3 text-right">Matched</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr
                    key={h.id}
                    className="border-b border-[var(--ssc-line)]/50"
                  >
                    <td className="py-2 pr-3 text-[var(--ssc-ink-2)]">
                      {h.uploaded_at?.slice(0, 19).replace("T", " ")}
                    </td>
                    <td className="py-2 pr-3 font-bold">{h.filename}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {h.match_counts?.total ?? 0}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums text-emerald-700">
                      {(h.match_counts?.matched_vin || 0) +
                        (h.match_counts?.matched_stock_fallback || 0)}
                    </td>
                    <td className="py-2 pr-3">
                      {h.published ? (
                        <span className="text-emerald-700 font-bold">
                          Published
                        </span>
                      ) : (
                        <span className="text-amber-700 font-bold">Draft</span>
                      )}
                    </td>
                    <td className="py-2 pr-3 flex gap-2">
                      <button
                        type="button"
                        onClick={() => openHistory(h.id)}
                        className="text-[var(--ssc-blue)] font-bold hover:underline"
                        data-testid={`byers-history-open-${h.id}`}
                      >
                        Open
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(h.id)}
                        className="text-red-700 font-bold hover:underline"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </div>
  );
}
